import { ReactComponent as IconDocumentSmall } from '@/assets/svgs/document-small.svg';
import classname from 'classnames';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Loader from 'react-loader-spinner';
import { uniqueId } from 'lodash';

const AttachmentDropzone = forwardRef(function AttachmentDropzone({
	addClass,
	title,
	subtitle,
	footnote,
	requiredAttachments = [],
	optionalAttachments = [],
	showAttachmentsContent = true,
	showDelete = false,
	showReplace = false,
	handleOnDeleteClick,
	dropzoneIcon,
	dropzoneTitle,
	footer,
	emptyLabel,
	parentCallback,
	isLoading = false,
	...dropzoneOptions
}, ref) {

	const [files, setFiles] = useState([]);
	const [requiredCounter, setRequiredCounter] = useState(0)

	const onDrop = useCallback(acceptedFiles => {

		const newFiles = acceptedFiles.map(file => Object.assign(file, {
			preview: URL.createObjectURL(file)
		}))

		setFiles(oldFiles => {
			if (dropzoneOptions?.multiple) {
				const checkedOldFiles = oldFiles.filter(oldFile => !newFiles.find(newFile => newFile.name === oldFile.name))
				const allFiles = checkedOldFiles.concat(newFiles)

				return allFiles
			} else {
				return acceptedFiles
			}
		})
	}, [])

	useImperativeHandle(ref, () => ({
		clearFiles() {
			setFiles([])
		}
	}));

	useEffect(() => {
		const required = files.filter(file => requiredAttachments.includes(file.name.replace(/\.[^/.]+$/, "")))
		const totalNumber = Math.min(required.length, 3)

		setRequiredCounter(totalNumber)

		if (required.length === requiredAttachments.length) {
			return parentCallback(files)
		}
		return parentCallback(false)
	}, [files])

	const { getRootProps, getInputProps } = useDropzone({ onDrop, ...dropzoneOptions })

	const RequiredAttachmentList = () => {

		const items = requiredAttachments.map((attachment, index) => {

			const active = files.some(file => file.name === `${attachment}.pdf` || file.name === `${attachment}.doc`)

			const visualCheckboxClass = classname({
				'visual-checkbox--active': active,
				'visual-checkbox': true
			})

			const id = uniqueId('attachment-dropzone__required-')

			return (
				<div className="attachment-dropzone__documents__content__col" key={id}>
					<div className="attachment-dropzone__document">
						<div className="attachment-dropzone__document__left"><div className={visualCheckboxClass}></div></div>
						<div className="attachment-dropzone__document__right">{attachment}</div>
					</div>
				</div>
			)
		})

		return items
	}

	const OptionalAttachmentList = () => {

		const items = optionalAttachments.map((attachment, index) => {

			const active = files.some(file => file.name === `${attachment}.pdf` || file.name === `${attachment}.doc`)

			const visualCheckboxClass = classname({
				'visual-checkbox': true,
				'visual-checkbox--active': active
			})

			const id = uniqueId('attachment-dropzone__optional-')

			return (
				<div className="attachment-dropzone__documents__content__col" key={id}>
					<div className="attachment-dropzone__document">
						<div className="attachment-dropzone__document__left"><div className={visualCheckboxClass}></div></div>
						<div className="attachment-dropzone__document__right">{attachment}</div>
					</div>
				</div>
			)
		})

		return items
	}

	const dropzoneClassname = classname(
		addClass,
		{
			"attachment-dropzone": true,
		}
	)

	dropzoneIcon = dropzoneIcon || <IconDocumentSmall />
	dropzoneTitle = dropzoneTitle || <p>Drop your files or <span className="t-primary-color">Browse</span></p>

	return (
		<>
			<div className={dropzoneClassname} {...getRootProps()}>

				{(isLoading) ?
					<Loader
						className="attachment-dropzone__loading"
						data-testid="attachment-dropzone__loading"
						type="ThreeDots"
						color="purple"
						height={10}
						width={15}
					/>
					: <>
						<input {...getInputProps()} data-testid="payload-dropzone" />
						<div className="attachment-dropzone__header">
							{title}
						</div>
						{showAttachmentsContent && (
							<div className="attachment-dropzone__content">
								<div className="attachment-dropzone__content__left">
									<div className="attachment-dropzone__documents">
										<div className="attachment-dropzone__documents__header">Required</div>
										<div className="attachment-dropzone__documents__content">
											<RequiredAttachmentList />
										</div>
									</div>
								</div>
								<div className="attachment-dropzone__content__right">
									<div className="attachment-dropzone__documents">
										<div className="attachment-dropzone__documents__header">Optional</div>
										<div className="attachment-dropzone__documents__content">
											<OptionalAttachmentList />
										</div>
									</div>
								</div>
							</div>
						)}
						<div className="attachment-dropzone__upload">
							<div className="attachment-dropzone__upload__icon">{dropzoneIcon}</div>
							{files.length === 0 && (
								<div className="attachment-dropzone__upload__content">{dropzoneTitle}</div>
							)}
							{files.length > 0 && (
								<div className="attachment-dropzone__upload__content">
									{showAttachmentsContent ? (
										<p>{requiredCounter} out of {requiredAttachments?.length} required files have been uploaded</p>
									) : (
										<p>{files?.map(s => s?.name).join(', ')}</p>
									)}
									{showReplace && (
										<span className="t-primary-color">Replace</span>
									)}
									{showDelete && (
										<span className="attachment-dropzone__upload__content__delete" onClick={(e) => {
											e.stopPropagation()
											handleOnDeleteClick()
										}}>Delete</span>
									)}
								</div>
							)}
							<div className="attachment-dropzone__upload__preview">
							</div>
						</div>
						{footer && (
							<div className="attachment-dropzone__footer">
								{footer}
							</div>
						)}
					</>
				}
			</div>

			{footnote && (
				<div className="attachment-dropzone-footnote">
					{footnote}
				</div>
			)}
		</>
	)
});

export default AttachmentDropzone
